export type MaterialRateType =
  | 'primer_l'
  | 'paint_l'
  | 'plaster_kg'
  | 'tile_adhesive_kg'
  | 'grout_kg'
  | 'hydro_kg'
  | 'silicone_szt'
  | 'paving_stone_m2'
  | 'aggregate_m3'
  | 'geotextile_m2'
  | 'sand_m3'
  | 'edge_mb';

export type MaterialRates = Partial<Record<MaterialRateType, number>>;

export type PriceRegion =
  | 'MVP_AVERAGE'
  | 'mazowieckie'
  | 'warminsko_mazurskie'
  | 'pomorskie'
  | 'slaskie'
  | 'malopolskie'
  | 'wielkopolskie';

export interface MaterialPrice {
  materialId: string;
  label: string;
  unit: 'l' | 'kg' | 'szt' | 'm²' | 'mb' | 'm³';
  price: number;
  source: 'default' | 'regional' | 'user';
  region?: PriceRegion;
}

export type MaterialPriceMap = Record<string, MaterialPrice>;

export const defaultMaterialRates: Record<MaterialRateType, number> = {
  primer_l: 12,
  paint_l: 35,
  plaster_kg: 2.2,
  tile_adhesive_kg: 1.8,
  grout_kg: 8,
  hydro_kg: 18,
  silicone_szt: 25,
  paving_stone_m2: 55,
  aggregate_m3: 120,
  geotextile_m2: 3,
  sand_m3: 80,
  edge_mb: 18,
};

export const DEFAULT_MATERIAL_RATES = defaultMaterialRates;

export const materialRateMeta: Record<MaterialRateType, { label: string; unit: MaterialPrice['unit'] }> = {
  primer_l: { label: 'Grunt', unit: 'l' },
  paint_l: { label: 'Farba', unit: 'l' },
  plaster_kg: { label: 'Gładź szpachlowa', unit: 'kg' },
  tile_adhesive_kg: { label: 'Klej do płytek', unit: 'kg' },
  grout_kg: { label: 'Fuga', unit: 'kg' },
  hydro_kg: { label: 'Hydroizolacja', unit: 'kg' },
  silicone_szt: { label: 'Silikon', unit: 'szt' },
  paving_stone_m2: { label: 'Kostka brukowa', unit: 'm²' },
  aggregate_m3: { label: 'Kruszywo', unit: 'm³' },
  geotextile_m2: { label: 'Geowłóknina', unit: 'm²' },
  sand_m3: { label: 'Piasek / podsypka', unit: 'm³' },
  edge_mb: { label: 'Obrzeża / krawężniki', unit: 'mb' },
};

// MVP regional estimates. Replace with supplier feed later.
export const regionalMaterialRates: Record<PriceRegion, { multiplier: number }> = {
  MVP_AVERAGE: { multiplier: 1 },
  mazowieckie: { multiplier: 1.15 },
  warminsko_mazurskie: { multiplier: 0.92 },
  pomorskie: { multiplier: 1.1 },
  slaskie: { multiplier: 1.05 },
  malopolskie: { multiplier: 1.08 },
  wielkopolskie: { multiplier: 1.03 },
};

export function resolveMaterialRate(
  type: MaterialRateType,
  userMaterialRates?: MaterialRates,
  localSupplierRates?: MaterialRates
): number {
  // TODO: podpiąć import CSV/XLSX z hurtowni jako localSupplierRates.
  return (
    userMaterialRates?.[type] ??
    localSupplierRates?.[type] ??
    DEFAULT_MATERIAL_RATES[type]
  );
}

export function resolveMaterialPrices(args: {
  region: PriceRegion;
  userOverrides?: Partial<Record<string, number>>;
}): MaterialPriceMap {
  const regionConfig = regionalMaterialRates[args.region] ?? regionalMaterialRates.MVP_AVERAGE;

  return Object.entries(defaultMaterialRates).reduce<MaterialPriceMap>(
    (prices, [materialId, defaultPrice]) => {
      const typedId = materialId as MaterialRateType;
      const userPrice = args.userOverrides?.[materialId];
      const hasRegionalPrice = args.region !== 'MVP_AVERAGE';
      const price =
        userPrice ??
        (hasRegionalPrice ? Math.round(defaultPrice * regionConfig.multiplier * 100) / 100 : defaultPrice);

      prices[materialId] = {
        materialId,
        label: materialRateMeta[typedId].label,
        unit: materialRateMeta[typedId].unit,
        price,
        source: userPrice !== undefined ? 'user' : hasRegionalPrice ? 'regional' : 'default',
        region: args.region,
      };

      return prices;
    },
    {}
  );
}
